/**
 * Structure comparison between the source HTML and the user-typed HTML.
 * Compares the *shape* of formatting the student produced against what
 * the source demonstrates. Not a strict AST diff — a lightweight signal
 * that shows whether the student remembered to preserve
 *   bold / italic / underline / center-align / justify.
 *
 * Returns { score: 0..100, checks: [{ id, label, expected, actual, ok }] }
 */

function _stripTags(html) {
  const d = document.createElement("div");
  d.innerHTML = html || "";
  return d.textContent || d.innerText || "";
}

function _countTags(html, selector) {
  if (!html) return 0;
  const d = document.createElement("div");
  d.innerHTML = html;
  return d.querySelectorAll(selector).length;
}

function _hasAlign(html, val) {
  if (!html) return false;
  const d = document.createElement("div");
  d.innerHTML = html;
  return [...d.querySelectorAll("*")].some((n) => {
    const s = (n.style && n.style.textAlign) || n.getAttribute("align");
    return s === val;
  });
}

export function compareFormatting(sourceHtml, typedHtml) {
  const has = (html, sel) => _countTags(html, sel) > 0;
  const checks = [
    { id: "bold",      label: "Bold usage",       expected: has(sourceHtml, "b, strong"),           actual: has(typedHtml, "b, strong") },
    { id: "italic",    label: "Italic usage",     expected: has(sourceHtml, "i, em"),               actual: has(typedHtml, "i, em") },
    { id: "underline", label: "Underline usage",  expected: has(sourceHtml, "u"),                    actual: has(typedHtml, "u") },
    { id: "center",    label: "Centered lines",   expected: _hasAlign(sourceHtml, "center"),        actual: _hasAlign(typedHtml, "center") },
    { id: "justify",   label: "Justified body",   expected: _hasAlign(sourceHtml, "justify"),       actual: _hasAlign(typedHtml, "justify") },
  ].filter((c) => c.expected || c.actual);   // only report the styles the source actually uses (or user tried)

  const scored = checks.map((c) => ({ ...c, ok: c.expected === c.actual }));
  const total = scored.length;
  const passed = scored.filter((c) => c.ok).length;
  const score = total === 0 ? 100 : Math.round((passed / total) * 100);
  return { score, checks: scored, plain: _stripTags(typedHtml) };
}

/**
 * Walks the DOM of an HTML string and emits one token per whitespace-separated
 * word, tagged with the inherited formatting context (bold/italic/underline/align).
 * Used by the Format Heatmap to compare formatting at word granularity.
 */
export function tokenizeWithFormat(html) {
  const root = document.createElement("div");
  root.innerHTML = html || "";
  const tokens = [];
  const norm = (a) => (a === "start" || !a ? "left" : a);

  function walk(node, ctx) {
    if (node.nodeType === 3 /* TEXT_NODE */) {
      const parts = (node.textContent || "").split(/(\s+)/);
      for (const p of parts) {
        if (!p || /^\s+$/.test(p)) continue;
        tokens.push({ word: p, ...ctx });
      }
      return;
    }
    if (node.nodeType !== 1 /* ELEMENT_NODE */) return;
    const tag = node.tagName.toLowerCase();
    const style = node.style || {};
    const inlineAlign = style.textAlign || node.getAttribute("align");
    const newCtx = {
      bold: ctx.bold || tag === "b" || tag === "strong"
            || style.fontWeight === "bold"
            || (parseInt(style.fontWeight, 10) >= 600),
      italic: ctx.italic || tag === "i" || tag === "em" || style.fontStyle === "italic",
      underline: ctx.underline || tag === "u" || (style.textDecoration || "").includes("underline"),
      align: norm(inlineAlign || ctx.align),
    };
    for (const child of node.childNodes) walk(child, newCtx);
  }
  walk(root, { bold: false, italic: false, underline: false, align: "left" });
  return tokens;
}

/**
 * Word-aligned formatting heatmap. Returns one cell per word position (up to the
 * longer of the two texts) with the list of formatting attributes that differ.
 * A "missing" cell means the student is short a word; "extra" means they typed
 * a word that isn't in the source.
 */
export function buildFormatHeatmap(sourceHtml, typedHtml) {
  const src = tokenizeWithFormat(sourceHtml);
  const usr = tokenizeWithFormat(typedHtml);
  const max = Math.max(src.length, usr.length);
  const cells = [];
  let mismatchCount = 0;
  for (let i = 0; i < max; i++) {
    const s = src[i];
    const u = usr[i];
    if (!s && u) { cells.push({ index: i, source: null, typed: u, mismatches: ["extra"] }); mismatchCount++; continue; }
    if (s && !u) { cells.push({ index: i, source: s, typed: null, mismatches: ["missing"] }); mismatchCount++; continue; }
    const mm = [];
    if (s.bold !== u.bold) mm.push("bold");
    if (s.italic !== u.italic) mm.push("italic");
    if (s.underline !== u.underline) mm.push("underline");
    if (s.align !== u.align) mm.push("align");
    if (mm.length) mismatchCount++;
    cells.push({ index: i, source: s, typed: u, mismatches: mm });
  }
  return { cells, mismatchCount, totalWords: max };
}
